/**
 * Vulnerable Populations API Service
 *
 * SWR hooks for fetching vulnerable group data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'
import type { VulnerableOverview } from '../../types/modules'

/**
 * Fetch vulnerable populations overview for a given year
 *
 * GET /vulnerable-populations?year={year}
 */
export function useVulnerableOverview(year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<VulnerableOverview>(
    [`/vulnerable-populations`, { year: year.toString() }],
    apiClient.fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: 5 * 60 * 1000, // 5 minutes
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch vulnerable population data for specific district
 *
 * GET /vulnerable-populations/by-district/{districtId}?year={year}
 */
export function useVulnerableByDistrict(districtId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR<VulnerableOverview>(
    districtId ? [`/vulnerable-populations/by-district/${districtId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch vulnerable group distribution across districts
 *
 * GET /vulnerable-populations/by-group/{groupId}?year={year}
 */
export function useVulnerableByGroup(groupId: number, year: number = 2567) {
  const { data, error, isLoading, mutate } = useSWR(
    groupId ? [`/vulnerable-populations/by-group/${groupId}`, { year: year.toString() }] : null,
    apiClient.fetcher
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch multi-year vulnerable population trends
 *
 * GET /vulnerable-populations/trends?district_id={id}&group_id={id}
 */
export function useVulnerableTrends(filters?: {
  districtId?: number
  groupId?: number
}) {
  const params: Record<string, string> = {}

  if (filters?.districtId) {
    params.district_id = filters.districtId.toString()
  }
  if (filters?.groupId) {
    params.group_id = filters.groupId.toString()
  }

  const { data, error, isLoading, mutate } = useSWR<{
    filters: typeof params
    trends: Array<{
      year: number
      total_population: string
      immediate_care: string
      receiving_support: string
    }>
  }>(
    [`/vulnerable-populations/trends`, params],
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60 * 1000, // Cache for 1 minute
    }
  )

  return {
    data,
    error,
    isLoading,
    mutate,
  }
}

/**
 * Fetch vulnerable groups (reference data)
 *
 * GET /vulnerable-groups
 */
export function useVulnerableGroups() {
  const { data, error, isLoading } = useSWR(
    '/vulnerable-groups',
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 60 * 60 * 1000, // Cache for 1 hour
    }
  )

  return {
    data,
    error,
    isLoading,
  }
}
